import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  customerValidateToken,
  customerRegister,
  customerMe,
  customerLinkOrganization,
  type ErrorResponse,
  type CustomerAuthResponse,
} from '@vn-propane/shared';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { LanguageSelector } from '@/components/ui/language-selector';
import { LegalFooter } from '@vn-propane/shared/components';
import { Loader2, CheckCircle2, Building2, CircleHelp } from 'lucide-react';

const MARKETING_BASE_URL = 'https://giaogas.com';

interface TokenInfo {
  customerName: string | null;
  organizationName: string | null;
}

export default function RegisterPage() {
  const { t, i18n } = useTranslation(['auth', 'common', 'errors']);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const orderId = searchParams.get('order_id');
  const emailFromUrl = searchParams.get('email');

  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null);
  const [isValidatingToken, setIsValidatingToken] = useState(!!token);
  const [tokenError, setTokenError] = useState<string | null>(null);
  // Manual code input when no token in URL
  const [manualCode, setManualCode] = useState('');

  // State for already logged-in users
  const [currentUser, setCurrentUser] = useState<CustomerAuthResponse | null>(null);
  const [isLinking, setIsLinking] = useState(false);
  const [linkSuccess, setLinkSuccess] = useState(false);

  const [username, setUsername] = useState('');
  const [email, setEmail] = useState(emailFromUrl || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  // The effective token: from URL or manual input
  const effectiveToken = token || manualCode.trim() || null;

  const legalUrl = (page: string) => {
    const langPrefix = i18n.language === 'en' ? '/en' : '';
    return `${MARKETING_BASE_URL}${langPrefix}/${page}/`;
  };

  // Check if user is already logged in and validate token (only when token in URL)
  useEffect(() => {
    async function initialize() {
      if (!token) {
        return;
      }

      try {
        // Check both auth status and token validity in parallel
        const [meResult, tokenResult] = await Promise.all([
          customerMe(),
          customerValidateToken({ query: { token } }),
        ]);

        // Check if token is valid
        if (tokenResult.data?.data?.valid) {
          setTokenInfo({
            customerName: tokenResult.data.data.customerName || null,
            organizationName: tokenResult.data.data.organizationName || null,
          });
        } else {
          setTokenError(t('customer.invalidToken'));
          setIsValidatingToken(false);
          return;
        }

        // Check if user is already logged in
        if (meResult.data?.data) {
          setCurrentUser(meResult.data.data);
        }
      } catch {
        setTokenError(t('customer.tokenValidationError'));
      } finally {
        setIsValidatingToken(false);
      }
    }

    initialize();
  }, [token, t]);

  // Handle linking for already logged-in users
  const handleLinkOrganization = async () => {
    if (!token) return;

    setIsLinking(true);
    setError(null);

    try {
      const result = await customerLinkOrganization({
        body: { token },
      });

      if (result.data) {
        setLinkSuccess(true);
        setTimeout(() => {
          if (orderId) {
            navigate(`/orders/${orderId}`);
          } else {
            navigate('/orders');
          }
        }, 1500);
      } else if (result.error) {
        const errorResponse = result.error as ErrorResponse;
        setError(errorResponse.error?.message || t('customer.linkFailed'));
      }
    } catch {
      setError(t('errors:unexpectedError'));
    } finally {
      setIsLinking(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!agreedToTerms) {
      setError(t('auth:legal.mustAgree'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('validation.passwordsDoNotMatch'));
      return;
    }

    if (password.length < 6) {
      setError(t('validation.passwordMustBeAtLeast'));
      return;
    }

    if (username.length < 3) {
      setError(t('validation.usernameMustBeAtLeast'));
      return;
    }

    setIsLoading(true);

    try {
      const result = await customerRegister({
        body: {
          token: effectiveToken || undefined,
          username,
          email: email.trim() || undefined,
          password,
        },
      });

      if (result.data) {
        setRegistrationSuccess(true);
        // Registration creates a session, redirect to order page if we have order_id
        setTimeout(() => {
          if (orderId) {
            navigate(`/orders/${orderId}`);
          } else {
            navigate('/orders');
          }
        }, 1500);
        return;
      }

      if (result.error) {
        const errorResponse = result.error as ErrorResponse;
        setError(errorResponse.error?.message || t('validation.registrationFailed'));
      } else {
        setError(t('validation.registrationFailed'));
      }
    } catch {
      setError(t('errors:unexpectedError'));
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state while validating token
  if (isValidatingToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/40">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
          <p className="mt-2 text-muted-foreground">{t('customer.validatingToken')}</p>
        </div>
      </div>
    );
  }

  // Show error if token is invalid
  if (tokenError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/40 relative">
        <div className="absolute top-4 right-4 flex items-center gap-2">
          <a
            href={i18n.language === 'en' ? `${import.meta.env.VITE_HELP_URL || '/help'}/en/customer/` : `${import.meta.env.VITE_HELP_URL || '/help'}/customer/`}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            title={t('common:navigation.help')}
          >
            <CircleHelp className="h-5 w-5" />
          </a>
          <LanguageSelector />
        </div>
        <div className="w-full max-w-md p-8 bg-background rounded-lg border shadow-sm">
          <h1 className="text-2xl font-extrabold tracking-tight mb-4 text-center text-destructive">
            {t('customer.invalidLinkTitle')}
          </h1>
          <Alert variant="destructive">
            <AlertDescription>{tokenError}</AlertDescription>
          </Alert>
          <p className="text-sm text-center mt-4 text-muted-foreground">
            {t('customer.contactSupport')}
          </p>
        </div>
      </div>
    );
  }

  // Show success message for registration
  if (registrationSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/40">
        <div className="w-full max-w-md p-8 bg-background rounded-lg border shadow-sm text-center">
          <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-extrabold tracking-tight mb-2">{t('customer.registrationSuccess')}</h1>
          <p className="text-muted-foreground">{t('customer.redirectingToOrder')}</p>
        </div>
      </div>
    );
  }

  // Show success message for linking
  if (linkSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/40">
        <div className="w-full max-w-md p-8 bg-background rounded-lg border shadow-sm text-center">
          <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-extrabold tracking-tight mb-2">{t('customer.linkSuccess')}</h1>
          <p className="text-muted-foreground">{t('customer.redirectingToOrder')}</p>
        </div>
      </div>
    );
  }

  // Show association confirmation for logged-in users
  if (currentUser && tokenInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/40 relative">
        <div className="absolute top-4 right-4 flex items-center gap-2">
          <a
            href={i18n.language === 'en' ? `${import.meta.env.VITE_HELP_URL || '/help'}/en/customer/` : `${import.meta.env.VITE_HELP_URL || '/help'}/customer/`}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            title={t('common:navigation.help')}
          >
            <CircleHelp className="h-5 w-5" />
          </a>
          <LanguageSelector />
        </div>
        <div className="w-full max-w-md p-8 bg-background rounded-lg border shadow-sm">
          <div className="text-center mb-6">
            <Building2 className="h-12 w-12 text-primary mx-auto mb-4" />
            <h1 className="text-2xl font-extrabold tracking-tight mb-2">{t('customer.addOrganization')}</h1>
            <p className="text-muted-foreground text-sm">
              {t('customer.addOrganizationDescription')}
            </p>
          </div>

          {/* Current user info */}
          <div className="mb-4 p-4 bg-muted/50 rounded-lg">
            <p className="text-sm text-muted-foreground">{t('customer.loggedInAs')}</p>
            <p className="font-medium">{currentUser.username || currentUser.phone}</p>
          </div>

          {/* Organization to link */}
          <div className="mb-6 p-4 bg-primary/10 rounded-lg border border-primary/20">
            {tokenInfo.customerName && (
              <p className="text-sm">
                <span className="text-muted-foreground">{t('customer.welcomeCustomer')}</span>{' '}
                <span className="font-medium">{tokenInfo.customerName}</span>
              </p>
            )}
            {tokenInfo.organizationName && (
              <p className="text-sm">
                <span className="text-muted-foreground">{t('customer.organization')}</span>{' '}
                <span className="font-medium">{tokenInfo.organizationName}</span>
              </p>
            )}
          </div>

          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-3">
            <Button
              onClick={handleLinkOrganization}
              disabled={isLinking}
              className="w-full"
            >
              {isLinking ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {t('customer.linking')}
                </>
              ) : (
                t('customer.confirmLink')
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/orders')}
              disabled={isLinking}
              className="w-full"
            >
              {t('common:cancel')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-muted/40 relative">
      <div className="absolute top-4 right-4 flex items-center gap-2">
        <a
          href={i18n.language === 'en' ? `${import.meta.env.VITE_HELP_URL || '/help'}/en/customer/` : `${import.meta.env.VITE_HELP_URL || '/help'}/customer/`}
          target="_blank"
          rel="noopener noreferrer"
          className="p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          title={t('common:navigation.help')}
        >
          <CircleHelp className="h-5 w-5" />
        </a>
        <LanguageSelector />
      </div>
      <div className="w-full max-w-md p-8 bg-background rounded-sm border shadow-sm">
        <div className="flex flex-col items-center mb-4">
          <img src={`${import.meta.env.BASE_URL}logo-mark-dark.png`} alt="Giao Gas" className="h-12 w-12 mb-2" />
          <h1 className="text-2xl font-extrabold tracking-tight text-center">
            giao<span className="text-propane-500">gas</span>
          </h1>
          <p className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mt-1">
            {t('customer.registration')}
          </p>
        </div>

        {/* Show customer and organization info */}
        {tokenInfo && (
          <div className="mb-6 p-4 bg-muted rounded-sm">
            {tokenInfo.customerName && (
              <p className="text-sm">
                <span className="text-muted-foreground">{t('customer.welcomeCustomer')}</span>{' '}
                <span className="font-medium">{tokenInfo.customerName}</span>
              </p>
            )}
            {tokenInfo.organizationName && (
              <p className="text-sm">
                <span className="text-muted-foreground">{t('customer.organization')}</span>{' '}
                <span className="font-medium">{tokenInfo.organizationName}</span>
              </p>
            )}
          </div>
        )}

        <p className="text-muted-foreground text-sm text-center mb-6">
          {t('customer.createAccountDescription')}
        </p>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Show manual code input when no token in URL */}
          {!token && (
            <div className="space-y-2">
              <Label htmlFor="registrationCode">
                {t('customer.registrationCodeLabel')}{' '}
                <span className="text-muted-foreground text-xs">({t('common:optional')})</span>
              </Label>
              <Input
                id="registrationCode"
                type="text"
                placeholder={t('customer.registrationCodePlaceholder')}
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value)}
                disabled={isLoading}
              />
              <p className="text-xs text-muted-foreground">
                {t('customer.registrationCodeHint')}
              </p>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="username">{t('fields.username')}</Label>
            <Input
              id="username"
              type="text"
              placeholder={t('placeholders.chooseUsername')}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              disabled={isLoading}
              autoComplete="username"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">
              {t('fields.email')} <span className="text-muted-foreground text-xs">({t('common:optional')})</span>
            </Label>
            <Input
              id="email"
              type="email"
              placeholder={t('placeholders.enterEmail')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              autoComplete="email"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">{t('fields.password')}</Label>
            <Input
              id="password"
              type="password"
              placeholder={t('placeholders.choosePassword')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
              autoComplete="new-password"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">{t('fields.confirmPassword')}</Label>
            <Input
              id="confirmPassword"
              type="password"
              placeholder={t('placeholders.confirmYourPassword')}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={isLoading}
              autoComplete="new-password"
            />
          </div>
          <div className="flex items-start gap-2">
            <Checkbox
              id="terms"
              checked={agreedToTerms}
              onCheckedChange={(checked) => setAgreedToTerms(checked === true)}
              disabled={isLoading}
              className="mt-0.5"
            />
            <Label htmlFor="terms" className="text-sm font-normal leading-snug cursor-pointer">
              {t('auth:legal.agreementPrefix')}{' '}
              <a href={legalUrl('terms')} target="_blank" rel="noopener noreferrer" className="text-primary underline hover:no-underline">
                {t('auth:legal.termsAndConditions')}
              </a>
              {' '}{t('auth:legal.agreementAnd')}{' '}
              <a href={legalUrl('privacy')} target="_blank" rel="noopener noreferrer" className="text-primary underline hover:no-underline">
                {t('auth:legal.privacyPolicy')}
              </a>
            </Label>
          </div>
          <Button type="submit" disabled={isLoading || !agreedToTerms} className="w-full">
            {isLoading ? t('registering') : t('register')}
          </Button>
        </form>
        <p className="text-sm text-center mt-4 text-muted-foreground">
          {t('links.alreadyHaveAccount')}{' '}
          <Link
            to={`/login${token ? `?token=${encodeURIComponent(token)}${orderId ? `&order_id=${encodeURIComponent(orderId)}` : ''}` : ''}`}
            className="text-primary hover:underline"
          >
            {t('signIn')}
          </Link>
        </p>
      </div>
      <LegalFooter className="absolute bottom-6" />
    </div>
  );
}
